import { createReducer, original, type Draft } from "@reduxjs/toolkit";
import arrayMove from "array-move";

import { assert } from "../../../asserts/index.ts";
import type {
  AddEffectAction,
  EffectBuild,
  RemoveEffectAction,
  RemoveNodeAction,
  RemoveResponseAction,
  ReorderEffectAction,
  ReplaceNodeAction,
  UpdateEffectConfigAction,
} from "../../../schemas/index.ts";
import type { EffectDesignsState } from "../../../schemas/nodeDesigns/effectDesignsStateSchema.ts";
import { extractNodeValueRefIds, extractRefIds, resetNodeValueRefs } from "../../../shared/index.ts";
import { InvalidActionError } from "../../InvalidActionError.ts";
import { removeNodeAction } from "../actions.ts";
import { removeIdFromLookup } from "./helpers/lookups.ts";
import {
  addEffectAction,
  removeEffectAction,
  removeResponseAction,
  reorderEffectAction,
  updateEffectAction,
} from "./triggerActions.ts";
import type { TriggersReducerOptions } from "./triggersReducerOptions.ts";

export type EffectAction =
  | AddEffectAction
  | RemoveEffectAction
  | ReorderEffectAction
  | RemoveResponseAction
  | ReplaceNodeAction
  | UpdateEffectConfigAction
  | RemoveNodeAction;

const validateAddEffectCreate =
  ({ isNodeIdValid }: Options) =>
  (action: AddEffectAction, effect: Pick<EffectBuild, "id" | "nodeId" | "targetNodeId" | "config">) => {
    if (!isNodeIdValid) return;
    if (!isNodeIdValid(effect.nodeId)) {
      throw new InvalidActionError(action, `Effect nodeId not found: ${effect.nodeId} on effect ${effect.id}.`);
    }
    if (effect.targetNodeId && !isNodeIdValid(effect.targetNodeId)) {
      throw new InvalidActionError(
        action,
        `Effect targetNodeId not found ${effect.targetNodeId} on effect ${effect.id}.`,
      );
    }
    if (effect.config.refs) {
      const invalidIds = extractRefIds(effect.config.refs).filter((id) => !isNodeIdValid(id));
      if (invalidIds.length > 0) {
        throw new InvalidActionError(action, `Invalid ref(s): ${invalidIds.join(",")} on effect ${effect.id}.`);
      }
    }
    const invalidValueRefIds = extractNodeValueRefIds(effect.config).filter((id) => !isNodeIdValid(id));
    if (invalidValueRefIds.length > 0) {
      throw new InvalidActionError(
        action,
        `Invalid value ref(s): ${invalidValueRefIds.join(",")} on effect ${effect.id}.`,
      );
    }
  };

interface Options {
  notifyEvent?: TriggersReducerOptions["notifyEvent"];
  isNodeIdValid?: (nodeId: number) => boolean;
}

export const effectsInitialState: EffectDesignsState = {
  byId: {},
  idsByResponseId: {},
  responseIdByEffectId: {},
};

export const effectsReducerCreate = ({ notifyEvent, isNodeIdValid }: Options) => {
  const validateAddEffect = validateAddEffectCreate({ isNodeIdValid });

  const removeEffect = (state: Draft<EffectDesignsState>, effectId: number) => {
    if (notifyEvent) {
      const effect = state.byId[effectId];
      const { buildableName, nodeId } = effect;
      const config = original(effect.config);
      notifyEvent({ type: "EFFECT_REMOVED", id: effectId, buildableName, config, nodeId });
    }
    const responseId = state.responseIdByEffectId[effectId];
    removeIdFromLookup(state.idsByResponseId, responseId, effectId);
    delete state.responseIdByEffectId[effectId];
    delete state.byId[effectId];
  };

  return createReducer(effectsInitialState, (builder) => {
    builder.addCase(addEffectAction, (state, action) => {
      const { responseId, effect } = action.payload;
      validateAddEffect(action, effect);

      if (notifyEvent) {
        const { id, buildableName, config, nodeId } = effect;
        notifyEvent({ type: "EFFECT_ADDED", id, buildableName, config, nodeId });
      }

      const effectId = effect.id;

      if (effectId in state.byId) {
        throw new InvalidActionError(action, `Effect already exists with id: ${effectId}.`);
      }

      if (effectId in state.responseIdByEffectId) {
        throw new Error(`Orphaned effect (id in a response but no state entry): ${JSON.stringify(action)}`);
      }

      for (const [_responseId, effectIds] of Object.entries(state.idsByResponseId)) {
        if (effectIds.includes(effectId)) {
          throw new Error(
            `Effect already exists on response: ${_responseId} with id: ${effectId} but no state entry: ${JSON.stringify(action)}.`,
          );
        }
      }

      if (!(responseId in state.idsByResponseId)) {
        state.idsByResponseId[responseId] = [];
      }
      state.idsByResponseId[responseId].push(effectId);
      state.responseIdByEffectId[effectId] = responseId;

      const { id, ...config } = effect;
      state.byId[id] = config;
    });

    builder.addCase(removeEffectAction, (state, action) => {
      const { id: effectId } = action.payload;
      if (!(effectId in state.byId)) return;
      assert(effectId in state.responseIdByEffectId, `Orphaned effect (not in a response): ${JSON.stringify(action)}`);
      removeEffect(state, effectId);
    });

    builder.addCase(updateEffectAction, (state, action) => {
      const { id: effectId, config } = action.payload;
      if (!state.byId[effectId]) {
        throw new InvalidActionError(action, `Effect not found to update with id: ${effectId}.`);
      }
      state.byId[effectId].config = config;
    });

    builder.addCase(reorderEffectAction, (state, action) => {
      const { id: effectId, newIndex } = action.payload;
      if (!(effectId in state.byId)) {
        throw new InvalidActionError(action, `Effect with id ${effectId} not found..`);
      }
      if (newIndex < 0) {
        throw new InvalidActionError(action, `New index must be greater than or equal to 0. Got ${newIndex}.`);
      }
      const responseId = state.responseIdByEffectId[effectId];
      if (!responseId) {
        throw new Error(`Effect in state but not in response ${JSON.stringify(action)}.`);
      }
      const effectIds = state.idsByResponseId[responseId];
      const index = effectIds.indexOf(effectId);
      if (index === -1) {
        throw new InvalidActionError(action, `No effect found with id "${effectId}" to move.`);
      }
      state.idsByResponseId[responseId] = arrayMove(effectIds, index, newIndex);
    });

    builder.addCase(removeResponseAction, (state, action) => {
      const { id: responseId } = action.payload;

      const effectIdsToRemove = state.idsByResponseId[responseId];
      if (!effectIdsToRemove?.length) return;
      delete state.idsByResponseId[responseId];

      for (const effectId of effectIdsToRemove) {
        removeEffect(state, effectId);
      }
    });

    builder.addCase(removeNodeAction, (state, action) => {
      const { id: nodeId } = action.payload;
      const effectIdsToRemove = Object.entries(state.byId)
        .filter(([, effect]) => effect.nodeId === nodeId || effect.targetNodeId === nodeId)
        .map(([effectId]) => Number(effectId));

      for (const effectId of effectIdsToRemove) {
        removeEffect(state, effectId);
      }

      // Reset any surviving effect's configured values (e.g. a "set to" value) that
      // referenced the deleted node so nothing dangles.
      for (const effect of Object.values(state.byId)) {
        resetNodeValueRefs(effect.config, nodeId);
      }
    });
  });
};
